import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer';
import { ITyphoon } from '../../shared/models/JTWC';
import { useTyphoonVisualizer } from './TyphoonVisualizer/hooks/useTyphoonVisualizer';
//import { TyphoonVisualizer } from './TyphoonVisualizer/TyphoonVisualizer';
import { TyphoonVisualizer } from './TyphoonVisualizer/TyphoonVisualizer';
import { fetchDisasterData, DisasterEvent } from '../services/disasterDataService';
import { MediaItem } from '../types/MediaItem';
import { createMediaSprite } from '../utils/mediaUtils';
import { getMediaItems } from '../services/mediaItemService'; // 追加
import styles from './Globe.module.css';
import debounce from 'lodash/debounce';
import DisasterCard from './DisasterCard'; 
import { fetchCFSVData, CFSVData } from '../services/cfsvDataService';

// windowオブジェクトにtwttrプロパティを追加
declare global {
  interface Window {
    twttr: any;
  }
}

interface GlobeProps {
  onMediaSelect: (media: MediaItem | null) => void;
}

const getAssetPath = (path: string) => {
  // パスの種類に応じて適切なプレフィックスを追加
  if (path.endsWith('.jpg') || path.endsWith('.jpeg')) {
    // 地球テクスチャ画像は /static/media 配下
    return `/static/media${path}`;
  } else if (path.includes('_icon.png')) {
    // アイコン画像は /static/images 配下
    return `/static/images${path}`;
  }
  return path;
};

const Globe: React.FC<GlobeProps> = ({ onMediaSelect }) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.Camera | null>(null);
  const mediaLabelsRef = useRef<CSS2DObject[]>([]);
  const eventMeshesRef = useRef<THREE.Mesh[]>([]);
  const spriteRef = useRef<THREE.Sprite[]>([]);
  const typhoonVisualizerRef = useRef<TyphoonVisualizer | null>(null);
  
  const [disasterEvents, setDisasterEvents] = useState<DisasterEvent[]>([]);
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]); // 追加
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredMedia, setHoveredMedia] = useState<MediaItem | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [typhoonData, setTyphoonData] = useState<ITyphoon | null>(null);

  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const [activeLabel, setActiveLabel] = useState<CSS2DObject | null>(null);

  const [selectedEvent, setSelectedEvent] = useState<DisasterEvent | null>(null);
  const [selectedMediaItems, setSelectedMediaItems] = useState<MediaItem[]>([]);

  // 新しいstateの追加
  const [showCFSVData, setShowCFSVData] = useState(true);
  const [cfsvType, setCFSVType] = useState<'temperature' | 'precipitation'>('temperature');
  const [showWeatherData, setShowWeatherData] = useState({
    temperature: true,
    precipitation: false
  });
  // CFSVデータ用のref
  const cfsvMeshRef = useRef<THREE.Mesh | THREE.Points | null>(null);
  const [forecastDate, setForecastDate] = useState<Date | null>(null);

  const handleMediaSelect = useCallback(
    (media: MediaItem | null) => {
      onMediaSelect(media);
    },
    [onMediaSelect] // 不要な依存関係を削除
  );

  useEffect(() => {
    console.log('hoveredMedia changed:', hoveredMedia);
    if (hoveredMedia && hoveredMedia.type === 'twitter') {
      // Twitterのウィジェットスクリプトを再実行
      window.twttr && window.twttr.widgets.load();
    }
  }, [hoveredMedia, isHovering]); // 依存関係を追加

  const handleMouseMove = useCallback(
    debounce((event: MouseEvent) => {
      const scene = sceneRef.current;
      const camera = cameraRef.current;
      const mediaLabels = mediaLabelsRef.current;
      const eventMeshes = eventMeshesRef.current;

      if (!scene || !camera) return;

      const mouse = new THREE.Vector2(
        (event.clientX / window.innerWidth) * 2 - 1,
        -(event.clientY / window.innerHeight) * 2 + 1
      );

      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(scene.children);

      // マウス位置を更新
      setMousePosition({ x: event.clientX, y: event.clientY });

      // すべてのメディアラベルを非表示にする
      mediaLabels.forEach((label) => ((label.element as HTMLDivElement).style.display = 'none'));

      // イベントマーカーのラベルを非表示にする
      eventMeshes.forEach((mesh) => {
        if (mesh.userData.labelDiv) {
          mesh.userData.labelDiv.style.visibility = 'hidden';
        }
      });

      for (let i = 0; i < intersects.length; i++) {
        const intersectedObject = intersects[i].object;
        if (intersectedObject.userData.mediaItem) {
          const index = mediaItems.findIndex(
            (item) => item.id === intersectedObject.userData.mediaItem.id
          );
          if (index !== -1) {
            (mediaLabels[index].element as HTMLDivElement).style.display = 'block';
            setHoveredMedia(intersectedObject.userData.mediaItem);
            setIsHovering(true);
            return;
          }
        } else if (intersectedObject.userData.labelDiv) {
          intersectedObject.userData.labelDiv.style.visibility = 'visible';
          setHoveredMedia(null);
          setIsHovering(false);
          return;
        }
      }

      setHoveredMedia(null);
      setIsHovering(false);

      // マウスが何も当たっていない場合、アクティブなラベル以外を非表示にする
      scene.children.forEach((child) => {
      if (child.userData && child.userData.labelDiv && child.userData.label !== activeLabel) {
        child.userData.labelDiv.style.visibility = 'hidden';
      }
    });

      console.log('Mouse move event:', { hoveredMedia, isHovering });
    }, 50),
    [setHoveredMedia, setIsHovering, setMousePosition, mediaItems]
  );

  // クリックとタッチのイベントハンドラを統合した関数
  const handleInteraction = useCallback((event: MouseEvent | TouchEvent) => {
    const currentScene = sceneRef.current;
    const currentCamera = cameraRef.current;
    
    if (!currentScene || !currentCamera || !mountRef.current) return;
    
    // クリック/タッチ座標の取得を改善
    let clientX: number;
    let clientY: number;
    
    // マウントされた要素の位置を取得
    const rect = mountRef.current.getBoundingClientRect();
  
    if (event instanceof MouseEvent) {
      clientX = event.clientX - rect.left;
      clientY = event.clientY - rect.top;
    } else {
      // TouchEventの場合
      event.preventDefault();
      if (event.changedTouches && event.changedTouches.length > 0) {
        clientX = event.changedTouches[0].clientX - rect.left;
        clientY = event.changedTouches[0].clientY - rect.top;
      } else {
        return;
      }
    }
  
    // 正規化された座標を計算
    const x = (clientX / rect.width) * 2 - 1;
    const y = -(clientY / rect.height) * 2 + 1;
    const mouseVector = new THREE.Vector2(x, y);
  
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouseVector, currentCamera);
    
    // オブジェクトの検出を最適化
    const intersects = raycaster.intersectObjects(
      currentScene.children.filter(child => child.userData && child.userData.event)
    );
  
    if (intersects.length > 0) {
      const intersectedObject = intersects[0].object;
      if (intersectedObject.userData && intersectedObject.userData.event) {
        const clickedEvent = intersectedObject.userData.event;
        
        // 状態更新をバッチ処理
        setSelectedEvent(clickedEvent);
        const relatedItems = mediaItems.filter(item => {
          const eventId = clickedEvent._id || clickedEvent.id;
          const itemEventId = typeof item.disasterEvent === 'string' 
            ? item.disasterEvent 
            : item.disasterEvent.$oid;
          return eventId === itemEventId;
        });
        setSelectedMediaItems(relatedItems);
      }
    } else {
      setSelectedEvent(null);
      setSelectedMediaItems([]);
    }
  }, [mediaItems]);

  useEffect(() => {
    console.log('Component mounted');
    console.log('Initial hoveredMedia:', hoveredMedia);
    console.log('Initial isHovering:', isHovering);

    // Twitterのウィジェットスクリプトを読み込む
    const script = document.createElement('script');
    script.src = 'https://platform.twitter.com/widgets.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    console.log('hoveredMedia changed:', hoveredMedia);
    if (hoveredMedia && hoveredMedia.type === 'twitter') {
      // Twitterのウィジェットスクリプトを再実行
      window.twttr && window.twttr.widgets.load();
    }
  }, [hoveredMedia]);

  useEffect(() => {
    console.log('isHovering changed:', isHovering);
  }, [isHovering]);

  useEffect(() => {
    setHoveredMedia(null);
    setIsHovering(false);
  }, []);

  useEffect(() => {
    let isComponentMounted = true;
  
    const fetchTyphoonData = async () => {
      try {
        console.log('Fetching typhoon data...');
        setIsLoading(true);
        const response = await fetch('/api/typhoons/latest');
        
        // コンポーネントがアンマウントされていないか確認
        if (!isComponentMounted) return;
  
        if (!response.ok) {
          throw new Error('Failed to fetch typhoon data');
        }
  
        const data: ITyphoon = await response.json();
        
        // コンポーネントがアンマウントされていないか確認
        if (!isComponentMounted) return;
        
        setTyphoonData(data);
        console.log('Typhoon data fetched successfully:', data);
      } catch (error) {
        console.error('Error fetching typhoon data:', error);
        if (isComponentMounted) {
          setError(error instanceof Error ? error.message : 'An error occurred');
        }
      } finally {
        if (isComponentMounted) {
          setIsLoading(false);
        }
      }
    };
  
    fetchTyphoonData();
  
    // クリーンアップ関数
    return () => {
      isComponentMounted = false;
    };
  }, []);

  // TyphoonVisualizerの初期化と更新を改善
  useEffect(() => {
    if (!sceneRef.current || !typhoonData) return;

    console.log('Initializing/Updating TyphoonVisualizer');
    
    // 既存のビジュアライザをクリーンアップ
    if (typhoonVisualizerRef.current) {
      typhoonVisualizerRef.current.cleanup();
    }

    try {
      // 新しいビジュアライザを作成
      typhoonVisualizerRef.current = new TyphoonVisualizer(sceneRef.current);
      
      // 描画を実行
      typhoonVisualizerRef.current.update(typhoonData, {
        showWindRadii: true,
        showForecastPath: true,
        pathColor: '#ff0000',
        windRadiiOpacity: 0.3
      });

      console.log('TyphoonVisualizer updated successfully');
    } catch (error) {
      console.error('Error updating TyphoonVisualizer:', error);
    }

    // クリーンアップ関数
    return () => {
      if (typhoonVisualizerRef.current) {
        typhoonVisualizerRef.current.cleanup();
      }
    };
  }, [typhoonData, sceneRef.current]); // sceneRef.currentの依存関係を追加

  useEffect(() => {
    console.log('Fetching disaster data...');
    fetchDisasterData()
      .then((events) => {
        console.log('Fetched disaster data:', events);
        setDisasterEvents(events);
      })
      .catch((err) => {
        console.error('Failed to fetch disaster data:', err);
        setError('Failed to load disaster data. Please try again later.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (sceneRef.current && typhoonData) {
      if (!typhoonVisualizerRef.current) {
        typhoonVisualizerRef.current = new TyphoonVisualizer(sceneRef.current);
      }
      typhoonVisualizerRef.current.update(typhoonData, { 
        showWindRadii: true, 
        showForecastPath: true,
        pathColor: '#ff0000', // 見やすい赤色に設定
        windRadiiOpacity: 0.3  // 半透明に設定 
        });
    }
  }, [typhoonData]);

  useEffect(() => {
    console.log('Fetching media items...');
    getMediaItems()
      .then((items) => {
        console.log('Fetched media items:', items);
        setMediaItems(items);
      })
      .catch((err) => {
        console.error('Failed to fetch media items:', err);
        setError('Failed to load media items. Please try again later.');
      });
  }, []);

  useEffect(() => {
    console.log('selectedMediaItems updated:', selectedMediaItems);
  }, [selectedMediaItems]); 

  useEffect(() => {
    const mount = mountRef.current;
    if (mountRef.current && !isLoading && !error) {
      const scene = new THREE.Scene();
      sceneRef.current = scene;
      scene.background = new THREE.Color(0x111111);
      const camera = new THREE.PerspectiveCamera(
        45,
        window.innerWidth / window.innerHeight,
        0.1,
        1000
      );
      cameraRef.current = camera;

      // カメラの初期位置を1箇所で設定
      camera.position.set(0, 0, 15);
      camera.lookAt(0, 0, 0);

      const renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true
      });
      renderer.setPixelRatio(window.devicePixelRatio);
      const labelRenderer = new CSS2DRenderer();

      const handleMouseMoveWrapper = (event: MouseEvent) => {
        handleMouseMove(event);
      };

      // renderer の設定
      renderer.setSize(window.innerWidth, window.innerHeight);
      mountRef.current.appendChild(renderer.domElement);

      labelRenderer.setSize(window.innerWidth, window.innerHeight);
      labelRenderer.domElement.style.position = 'absolute';
      labelRenderer.domElement.style.top = '0';
      labelRenderer.domElement.style.pointerEvents = 'none';
      mountRef.current.appendChild(labelRenderer.domElement);

      // 地球儀のテクスチャを追加
      const textureLoader = new THREE.TextureLoader();
      const earthTexture = textureLoader.load(getAssetPath('/earth_blue_marble.jpg'));
      const geometry = new THREE.SphereGeometry(5.5, 64, 64);

      // 光源を追加
      const ambientLight = new THREE.AmbientLight(0x404040, 0.5);
      scene.add(ambientLight);

      // メインの光源
      const mainLight = new THREE.DirectionalLight(0xffffff, 1.0);
      mainLight.position.set(5, 3, 5);
      scene.add(mainLight);

      // 補助光源を追加して影を軽減
      const fillLight = new THREE.DirectionalLight(0xffffff, 0.3);
      fillLight.position.set(-5, -3, -5);
      scene.add(fillLight);

      // 半球光源を追加してグローバルな照明を改善
      const hemisphereLight = new THREE.HemisphereLight(0xffffbb, 0x080820, 0.5);
      scene.add(hemisphereLight);

      // レンダラーの設定も確認
      renderer.setClearColor(0x000000, 0);    // 背景を完全な透明に
      renderer.setPixelRatio(window.devicePixelRatio);
      //renderer.gammaOutput = true;            // ガンマ補正を有効に
      renderer.gammaFactor = 2.2;            // 標準的なガンマ値

      // 地球のマテリアル
      const earthMaterial = new THREE.MeshPhongMaterial({
        map: earthTexture,
        bumpScale: 0.1,
        specular: new THREE.Color(0x222222),  // スペキュラー反射を抑える
        shininess: 5,                         // 光沢を抑える
        transparent: false,
        opacity: 1.0,
        combine: THREE.MultiplyOperation,     // テクスチャの合成方法を指定
        side: THREE.FrontSide                 // 表面のみをレンダリング
      });
      const sphere = new THREE.Mesh(geometry, earthMaterial);
      scene.add(sphere);

      // カメラの位置設定
      camera.position.z = 12;

      // イベントリスナーの設定
      renderer.domElement.addEventListener('click', handleInteraction);
      renderer.domElement.addEventListener('touchstart', handleInteraction);

      // OrbitControlsを追加
      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controls.rotateSpeed = 0.5;
      controls.enableZoom = true;
      controls.enablePan = false;
      controls.autoRotate = true;
      controls.autoRotateSpeed = 0.5;
      //controls.minDistance = 10;
      //controls.maxDistance = 15;

      // ズームの制限を設定
      controls.minDistance = 10; // より近いズームイン制限
      controls.maxDistance = 20; // より近いズームアウト制限

      // カメラの視野角の制限を設定
      camera.fov = Math.min(45, Math.max(30, camera.fov));
      camera.updateProjectionMatrix();

      // タッチ操作の設定を修正
      if (typeof window !== 'undefined' && 'ontouchstart' in window) {
        controls.rotateSpeed = 0.3; // モバイルデバイスの場合は回転速度を少し遅く
        controls.enableDamping = true; // 慣性を有効化
        controls.dampingFactor = 0.1; // 慣性の強さを調整
      }

      // タッチデバイス用の設定
      let touchStartTime = 0;
      let touchStartPosition = { x: 0, y: 0 };
      let isTapping = false;

      renderer.domElement.addEventListener('touchstart', (e: TouchEvent) => {
        // シングルタッチのみ処理
        if (e.touches.length === 1) {
          touchStartTime = Date.now();
          touchStartPosition = {
            x: e.touches[0].clientX,
            y: e.touches[0].clientY
          };
          isTapping = true;
        }
      });

      renderer.domElement.addEventListener('touchmove', (e: TouchEvent) => {
        // タッチ移動距離が大きい場合は回転操作とみなす
        if (isTapping && e.touches.length === 1) {
          const moveX = Math.abs(e.touches[0].clientX - touchStartPosition.x);
          const moveY = Math.abs(e.touches[0].clientY - touchStartPosition.y);
          
          if (moveX > 10 || moveY > 10) {
            // 回転操作と判定
            isTapping = false;
            controls.autoRotate = false;
          }
        }
      });

      renderer.domElement.addEventListener('touchend', (e: TouchEvent) => {
        const touchDuration = Date.now() - touchStartTime;
        
        if (isTapping && touchDuration < 200) {
          // タップ操作として処理
          e.preventDefault(); // デフォルトの動作を防止
          handleInteraction(e);
        }
        
        // 自動回転の制御
        setTimeout(() => {
          controls.autoRotate = true;
        }, touchDuration < 200 ? 0 : 1000); // タップの場合は即座に、ドラッグの場合は遅延して再開      
        
        isTapping = false;
      });

      camera.position.z = 15;
      camera.position.set(0, 0, 20); // Z軸の値を適切に調整
      camera.lookAt(0, 0, 0);

      const raycaster = new THREE.Raycaster();
      const mouse = new THREE.Vector2();

      // 災害イベントをマッピング
      disasterEvents.forEach((event) => {
        const { icon, color } = getEventIconAndColor(event.eventType, event.alertLevel);
        const texture = new THREE.TextureLoader().load(icon);

        // PlaneGeometryを使用してフラットな四角形を作成
        const planeGeometry = new THREE.PlaneGeometry(0.4, 0.4);
        const planeMaterial = new THREE.MeshBasicMaterial({
          map: texture,
          color: color,
          transparent: true,
          opacity: 0.9,
          depthWrite: false,
          depthTest: true,
          side: THREE.DoubleSide
        });
        
        const plane = new THREE.Mesh(planeGeometry, planeMaterial);
        
        // 地球の表面からの距離を設定
        const earthRadius = 5.5;
        const iconOffset = 0.1;
        const position = latLongToVector3(event.lat, event.lon, earthRadius + iconOffset);
        plane.position.copy(position);
        
        // アイコンを地球の表面に向けて配置
        plane.lookAt(new THREE.Vector3(0, 0, 0));
        // 上方向を調整（必要に応じて）
        plane.up.set(0, 1, 0);
        
        plane.renderOrder = 1000;
        scene.add(plane);
        plane.userData = { event: event };

        /*
        const spriteMaterial = new THREE.SpriteMaterial({ 
          map: spriteMap, 
          color: color, 
          sizeAttenuation: false, 
          depthWrite: false,  
          depthTest: true,
          transparent: true,     
          opacity: 0.9      
        });
        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.scale.set(0.05, 0.05, 1);
        //sprite.scale.set(0.1, 0.1, 1);

        //const position = latLongToVector3(event.lat, event.lon, 5.51);
        // 地球の表面からの距離を調整
        const earthRadius = 5.5;
        const spriteOffset = 0.1;
        const spritePosition = latLongToVector3(event.lat, event.lon, earthRadius + spriteOffset);
        sprite.position.copy(spritePosition);

        // スプライトが常にカメラの方を向くように設定
        //sprite.material.depthTest = false;
        sprite.renderOrder = 1000;

        // レイキャストの対象にするために renderOrder を設定
        sprite.renderOrder = 999; // 高い値を設定して最後に描画
        
        scene.add(sprite);
        sprite.userData = { event: event };
        */
      });

      // クリックイベントハンドラ
      const handleMouseClick = (event: MouseEvent) => {
        mouse.x = (event.clientX / renderer.domElement.clientWidth) * 2 - 1;
        mouse.y = -(event.clientY / renderer.domElement.clientHeight) * 2 + 1;
      
        raycaster.setFromCamera(mouse, camera);
        const intersects = raycaster.intersectObjects(scene.children);

        console.log('Intersects:', intersects); // クリックされたオブジェクトのログ
      
        if (intersects.length > 0) {
          const intersectedObject = intersects[0].object;
          if (intersectedObject.userData && intersectedObject.userData.event) {
            const clickedEvent = intersectedObject.userData.event;
            console.log('Clicked event:', clickedEvent);
            setSelectedEvent(clickedEvent);
            
            // 関連するメディアアイテムを検索
            const relatedMediaItems = mediaItems.filter(item => {
              console.log('Comparing item:', item);
              console.log('Clicked event _id:', clickedEvent._id);
              console.log('Clicked event id:', clickedEvent.id);
              console.log('Item disasterEvent:', item.disasterEvent);
              
              // MongoDB の _id で比較
              if (clickedEvent._id && typeof item.disasterEvent === 'string') {
                return item.disasterEvent === clickedEvent._id;
              }
              // GDACS の id で比較
              else if (typeof item.disasterEvent === 'string') {
                return item.disasterEvent === clickedEvent.id;
              }
              // ObjectId の場合
              else if (typeof item.disasterEvent === 'object' && item.disasterEvent.$oid) {
                return item.disasterEvent.$oid === (clickedEvent._id || clickedEvent.id);
              }
              
              return false;
            });
    
            console.log('Related media items:', relatedMediaItems);
            setSelectedMediaItems(relatedMediaItems);
          }
        } else {
          setSelectedEvent(null);
          setSelectedMediaItems([]);
        }
      };

      //window.addEventListener('mousemove', handleMouseMove);
      renderer.domElement.addEventListener('mousemove', handleMouseMoveWrapper);
      window.addEventListener('mousemove', handleMouseMoveWrapper);

      const animate = () => {
        requestAnimationFrame(animate);

        // スプライトの表示制御
        scene.children.forEach(child => {
          if (child instanceof THREE.Sprite) {
            const spritePos = child.position.clone();
            const camToSprite = spritePos.clone().sub(camera.position);
            const camToEarthCenter = new THREE.Vector3(0, 0, 0).sub(camera.position);
            
            // スプライトが地球の表側にあるかどうかを判定
            const isVisible = camToSprite.angleTo(camToEarthCenter) < Math.PI / 2;
            
            // 地球の縁に近づくほど若干透明になる（ただし最小値を0.7に設定）
            const angle = camToSprite.angleTo(camToEarthCenter);
            const opacity = Math.max(0.7, Math.cos(angle));
            
            child.visible = isVisible;
            if (child.material instanceof THREE.SpriteMaterial) {
              child.material.opacity = opacity;
            }
          }
        });
        controls.update();
        renderer.render(scene, camera);
        labelRenderer.render(scene, camera);
      };

      animate();

      const handleResize = () => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        camera.aspect = width / height;
        
        // アスペクト比に応じてFOVを動的に調整
        //if (width < height) {
        //  camera.fov = 45;
        //} else {
        //  camera.fov = 40;
        //}
        
        camera.updateProjectionMatrix();
        renderer.setSize(width, height);
        labelRenderer.setSize(width, height);
        controls.update();
      };

      // メディアスプライトを追加
      /*
      const mediaLabels: CSS2DObject[] = [];
      mediaItems.forEach((item) => {
        const sprite = createMediaSprite(item);
        scene.add(sprite);

        const labelDiv = document.createElement('div');
        labelDiv.className = styles.mediaLabel;
        labelDiv.style.display = 'none';
        const img = document.createElement('img');
        img.src = item.url;
        img.style.width = '200px';
        img.style.height = '200px';
        img.style.objectFit = 'cover';
        img.onload = () => {
          img.style.display = 'none';
        };
        img.onerror = () => {
          labelDiv.textContent = 'Image not available';
        };
        labelDiv.appendChild(img);

        const label = new CSS2DObject(labelDiv);
        label.visible = false;
        const position = sprite.position.clone().multiplyScalar(1.2);
        label.position.copy(position);
        scene.add(label);
        mediaLabels.push(label);

        // スプライトにメディア情報を関連付ける
        sprite.userData.mediaItem = item;
      });
      mediaLabelsRef.current = mediaLabels;
      */

      // マウスイベントリスナーを追加
      window.addEventListener('mousemove', handleMouseMoveWrapper);
      window.addEventListener('click', handleMouseClick);
      window.addEventListener('resize', handleResize);

      return () => {
        renderer.domElement.removeEventListener('click', handleInteraction);
        renderer.domElement.removeEventListener('touchstart', handleInteraction);
        window.removeEventListener('mousemove', handleMouseMoveWrapper);
        window.removeEventListener('click', handleMouseClick);
        window.removeEventListener('resize', handleResize);
        if (mount) {
          mount.removeChild(renderer.domElement);
          mount.removeChild(labelRenderer.domElement);
        }
      };
    }
  }, [disasterEvents, isLoading, error, handleMediaSelect, onMediaSelect, handleMouseMove, mediaItems]);

  /*
  useEffect(() => {
    if (!showWeatherData[cfsvType]) return;
  
    const fetchData = async () => {
      try {
        console.log('Fetching CFSV data for:', cfsvType);
        const data = await fetchCFSVData(cfsvType);
        
        // データから最新の日付を取得
        if (data && data.length > 0) {
          const latestDate = new Date(data[0].date);
          console.log("latestDate: ", data[0]);
          setForecastDate(latestDate);
        }
        console.log("latestDate2: ");

        // テクスチャとして使用するために、バックエンドからマップ画像を取得
        const textureLoader = new THREE.TextureLoader();
        textureLoader.load(
          `/api/cfsv-data/${cfsvType}/map`,
          (texture) => {
            // テクスチャの設定
            texture.minFilter = THREE.LinearFilter;
            texture.magFilter = THREE.LinearFilter;
            
            // 既存のCFSVメッシュを削除
            if (cfsvMeshRef.current && sceneRef.current) {
              sceneRef.current.remove(cfsvMeshRef.current);
              cfsvMeshRef.current = null;
            }
  
            // 新しいメッシュを作成
            const geometry = new THREE.SphereGeometry(5.6, 64, 32);
            const material = new THREE.MeshBasicMaterial({
              map: texture,
              transparent: true,
              opacity: 0.7,
              blending: THREE.AdditiveBlending
            });
  
            const mesh = new THREE.Mesh(geometry, material);
            
            if (sceneRef.current) {
              sceneRef.current.add(mesh);
              cfsvMeshRef.current = mesh;
            }
          },
          undefined,
          (error) => {
            console.error('Error loading CFSV texture:', error);
          }
        );
      } catch (error) {
        console.error('Error in CFSV data effect:', error);
      }
    };
  
    fetchData();
  
    // クリーンアップ関数
    return () => {
      if (cfsvMeshRef.current && sceneRef.current) {
        sceneRef.current.remove(cfsvMeshRef.current);
        cfsvMeshRef.current = null;
      }
    };
  }, [cfsvType, showWeatherData]);

  if (isLoading) {
    return <div>Loading disaster data...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }
    */

  const renderMediaItem = (mediaItem: MediaItem) => {
    console.log('Rendering media item:', mediaItem); // デバッグ用ログ
    switch (mediaItem.type) {
      case 'youtube':
        if (mediaItem.youtubeEmbed) {
          return (
            <div key={mediaItem.id} className={styles.mediaItem}>
              <div dangerouslySetInnerHTML={{ __html: mediaItem.youtubeEmbed }} />
            </div>
          );
        } else {
          const videoId = mediaItem.url.split('v=')[1] || mediaItem.id;
          return (
            <div key={mediaItem.id} className={styles.mediaItem}>
              <iframe
                width="280"
                height="157"
                src={`https://www.youtube.com/embed/${videoId}`}
                frameBorder="0"
                allow="autoplay; encrypted-media"
                allowFullScreen
              ></iframe>
            </div>
          );
        }
      case 'twitter':
        return (
          <div key={mediaItem.id} className={styles.mediaItem}>
            <div dangerouslySetInnerHTML={{ __html: mediaItem.twitterEmbed || '' }} />
          </div>
        );
      default:
        console.log('Unknown media type:', mediaItem.type);
        return null;
    }
  };

  // renderControlsの追加
  const renderControls = () => (
    <div className={styles.controlsContainer}>
      <div className={styles.controlsInner}
           style={{
             background: 'rgba(30, 41, 59, 0.75)',
             backdropFilter: 'blur(8px)',
             border: '1px solid rgba(255, 255, 255, 0.1)',
             boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
             padding: '1rem',
             borderRadius: '0.5rem',
           }}>
        <h3 className="text-xl font-semibold text-white mb-3">Weather Data</h3>
          {forecastDate && (
          <p className="text-sm text-white/80 mb-2">
            AsIs: {forecastDate.toLocaleDateString()}
          </p>
        )}
        <select
          value={cfsvType}
          onChange={(e) => {
            e.stopPropagation();
            const newType = e.target.value as 'temperature' | 'precipitation';
            setCFSVType(newType);
            setShowWeatherData({
              temperature: newType === 'temperature',
              precipitation: newType === 'precipitation'
            });
          }}
          className="w-full px-3 py-2 bg-white/15 border border-white/20 rounded-md
                     text-white/90
                     hover:bg-white/20 focus:bg-white/20
                     transition-colors duration-200
                     cursor-pointer outline-none"
        >
          <option value="temperature" className="bg-slate-800 text-white">Temperature</option>
          <option value="precipitation" className="bg-slate-800 text-white">Precipitation</option>
        </select>
      </div>
    </div>
  );

  return (
    <div className={styles.globeContainer}>
      <div className={styles.canvasWrapper} ref={mountRef} />
      <header className={styles.header}>
        <div className={styles.logoContainer}>
          <img src="/static/images/logo.png" alt="ClimaSentry" className={styles.logo} />
          <span className={styles.logoText}>ClimaSentry</span>
        </div>
      </header>
      
      {/* コントロールを追加 */}
      {/*renderControls()*/}
      {/*}
      <div className={styles.controlsContainer}>
        <div className={styles.controlsInner}>
          <p className="mb-2 select-none text-white">Weather Forecast</p>
          <select
            value={cfsvType}
            onChange={(e) => {
              e.stopPropagation();
              setCFSVType(e.target.value as 'temperature' | 'precipitation');
              setShowWeatherData({
                temperature: e.target.value === 'temperature',
                precipitation: e.target.value === 'precipitation'
              });
            }}
            className="bg-white/10 p-2 rounded w-full cursor-pointer text-white"
          >
            <option value="temperature">Temperature</option>
            <option value="precipitation">Precipitation</option>
          </select>
        </div>
      </div>
      */}

      {selectedEvent && (
        <div className={styles.infoContainer}>
          {selectedMediaItems.length > 0 ? (
            <div className={styles.mediaContainer}>
              <h3 className={styles.cardTitle}>{selectedEvent.title}</h3>
              <div className={styles.mediaItemsContainer}>
                {selectedMediaItems.map(item => (
                  <div key={item.id} className={styles.mediaItem}>
                    {renderMediaItem(item)}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <DisasterCard 
              event={selectedEvent} 
              mediaItems={selectedMediaItems}
              // styles.disasterCardを直接渡すのではなく、Tailwindのクラスを使用
              className="touch-none backdrop-blur-lg bg-black/75 rounded-lg shadow-lg"
            />
          )}
        </div>
      )}
            
      {/*(showWeatherData.temperature || showWeatherData.precipitation) && showCFSVData && (
        <ColorLegend 
          type={showWeatherData.temperature ? 'temperature' : 'precipitation'} 
        />
      )*/}

      {/* ズームコントロールやその他のUIコンポーネント */}
      <div className="fixed bottom-4 left-4 z-50">
        {/* ここにズームコントロールなどを配置可能 */}
      </div>
    </div>
  );
};

// 緯度経度を3D座標に変換する関数
function latLongToVector3(lat: number, lon: number, radius: number): THREE.Vector3 {
  console.log('latLongToVector3 input:', lat, lon, radius); // デバッグ用ログ
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);
  const x = -(radius * Math.sin(phi) * Math.cos(theta));
  const z = radius * Math.sin(phi) * Math.sin(theta);
  const y = radius * Math.cos(phi);
  const result = new THREE.Vector3(x, y, z);
  console.log('latLongToVector3 output:', result); // デバッグ用ログ
  return result;
}

// アラートレベルに基づいて色を返す関数
function getEventColor(alertLevel: string): number {
  switch (alertLevel.toLowerCase()) {
    case 'red':
      return 0xff0000;
    case 'orange':
      return 0xffa500;
    case 'green':
      return 0x00ff00;
    default:
      return 0xffffff;
  }
}

// 災害タイプに応じたアイコンと色を返す関数
function getEventIconAndColor(type: string, alertLevel: string): { icon: string; color: number } {
  let icon = '';
  let color = 0xffffff; // デフォルトの色

  switch (type) {
    case 'EQ':
      icon = getAssetPath('/earthquake_icon.png');
      color = alertLevel.toLowerCase() === 'red' ? 0xff0000 : alertLevel.toLowerCase() === 'orange' ? 0xffa500 : 0x00ff00;
      break;
    case 'TC':
      icon = getAssetPath('/tropical_cyclones_icon.png');
      color = alertLevel.toLowerCase() === 'red' ? 0xff0000 : alertLevel.toLowerCase() === 'orange' ? 0xffa500 : 0x00ff00;
      break;
    case 'FL':
      icon = getAssetPath('/floods_icon.png');
      color = alertLevel.toLowerCase() === 'red' ? 0xff0000 : alertLevel.toLowerCase() === 'orange' ? 0xffa500 : 0x00ff00;
      break;
    case 'VL':
      icon = getAssetPath('/volcanoes_icon.png');
      color = alertLevel.toLowerCase() === 'red' ? 0xff0000 : alertLevel.toLowerCase() === 'orange' ? 0xffa500 : 0x00ff00;
      break;
    case 'DR':
      icon = getAssetPath('/drought_icon.png');
      color = alertLevel.toLowerCase() === 'red' ? 0xff0000 : alertLevel.toLowerCase() === 'orange' ? 0xffa500 : 0x00ff00;
      break;
    case 'WF':
      icon = getAssetPath('/forest_fire_icon.png');
      color = alertLevel.toLowerCase() === 'red' ? 0xff0000 : alertLevel.toLowerCase() === 'orange' ? 0xffa500 : 0x00ff00;
      break;
    default:
      icon = getAssetPath('/default.png');
      break;
  }

  return { icon, color };
}

const ColorLegend = ({ type }: { type: 'temperature' | 'precipitation' }) => {
  const legendData = type === 'temperature'
    ? [
        { value: '-4℃', color: 'rgb(0, 0, 255)' },
        { value: '-2℃', color: 'rgb(102, 102, 255)' },
        { value: '0℃', color: 'rgb(255, 255, 255)' },
        { value: '2℃', color: 'rgb(255, 153, 153)' },
        { value: '4℃', color: 'rgb(255, 0, 0)' },
      ]
    : [
        { value: '-10mm', color: 'rgb(139, 0, 0)' },
        { value: '-6mm', color: 'rgb(255, 0, 0)' },
        { value: '-2mm', color: 'rgb(255, 140, 0)' },
        { value: '0mm', color: 'rgb(255, 255, 255)' },
        { value: '2mm', color: 'rgb(144, 238, 144)' },
        { value: '6mm', color: 'rgb(34, 139, 34)' },
        { value: '10mm', color: 'rgb(0, 64, 0)' },
      ];

  return (
    <div
      style={{
        position: 'absolute',
        bottom: '100px',
        left: '20px',
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(8px)',
        padding: '12px',
        borderRadius: '8px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        color: 'white',
        fontSize: '14px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        zIndex: 50,
      }}
    >
      <div className="mb-2 font-semibold">
        {type === 'temperature' ? 'Temperature' : 'Precipitation'}
      </div>
      {legendData.map(({ value, color }) => (
        <div
          key={value}
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '5px',
          }}
        >
          <div
            style={{
              backgroundColor: color,
              width: '20px',
              height: '20px',
              marginRight: '10px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '4px',
            }}
          ></div>
          <span>{value}</span>
        </div>
      ))}
    </div>
  );
};

export default Globe;